import React, { useState } from 'react';
import { Space, Switch, Tooltip, Typography, message } from 'antd';

import { LaunchAccessAnswer, setLaunchAccess } from './api';

/**
 * One trainer's "Launch from Scenario" switch, for the Trainers admin screen.
 *
 * What it shows beside the switch matters as much as the switch: an admin
 * asking "why can Ana launch and Bogdan not?" gets a sentence, not a shrug.
 * The two states that look identical from outside — on because somebody
 * granted it, and on because the trainer runs a class with trainees — read
 * differently here, because turning the second one off is a withdrawal that
 * has to survive the class filling up again.
 */

const SOURCE_LABEL: Record<string, string> = {
  admin: 'Administrator',
  granted: 'Granted',
  revoked: 'Withdrawn',
  class_default: 'On by default',
  no_class: 'Off by default',
  not_a_trainer: 'Not a trainer',
};

export type LaunchAccessSwitchProps = {
  row: LaunchAccessAnswer;
  onChanged: (updated: LaunchAccessAnswer) => void;
};

const LaunchAccessSwitch: React.FC<LaunchAccessSwitchProps> = ({
  row,
  onChanged,
}) => {
  const [saving, setSaving] = useState(false);
  const locked = row.source === 'admin' || row.source === 'not_a_trainer';

  const toggle = async (next: boolean) => {
    if (row.user_id == null) return;
    setSaving(true);
    try {
      const updated = await setLaunchAccess(row.user_id, next);
      onChanged({ ...row, ...updated });
      message.success(
        next
          ? `${row.username || 'This trainer'} can now launch a scenario into their class.`
          : `${row.username || 'This trainer'} can no longer launch a scenario.`,
      );
    } catch (error) {
      message.error((error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const detail = [
    row.explanation,
    row.record?.granted_by && row.record?.granted_at && row.allowed
      ? `Granted by ${row.record.granted_by} on ${new Date(row.record.granted_at).toLocaleString()}.`
      : '',
    row.record?.revoked_by && row.record?.revoked_at && !row.allowed
      ? `Withdrawn by ${row.record.revoked_by} on ${new Date(row.record.revoked_at).toLocaleString()}.`
      : '',
    row.record?.note ? `Note: ${row.record.note}` : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Space direction="vertical" size={0}>
      <Tooltip title={locked ? row.explanation : detail}>
        <Switch
          checked={row.allowed}
          loading={saving}
          disabled={locked}
          onChange={toggle}
          aria-label={`Launch from Scenario for ${row.username || row.user_id}`}
          checkedChildren="On"
          unCheckedChildren="Off"
        />
      </Tooltip>
      <Typography.Text type="secondary">
        {SOURCE_LABEL[row.source] || row.source}
      </Typography.Text>
    </Space>
  );
};

export default LaunchAccessSwitch;
